import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, Row, Col, DatePicker, Upload, Popover, Card } from 'antd';
import { Authorize, Button } from 'app/atoms';
import {
  ItalicOutlined,
  BoldOutlined,
  PictureOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  AlignLeftOutlined,
  FontColorsOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/lib/form';
import { fieldDefaultProps } from 'utils/fieldConfig';
import { formatDateWithLocale, renderDateWithLocale } from 'utils/date-time';
import {
  DATE_FORMAT,
  IMAGE_TYPE_WITHOUT_PDF,
  isDisableBeforeCurrentDate,
  MAX_PROFILE_PICTURE_COUNT,
} from 'configs/constants';
import { validateUploadSize } from 'utils/validateUploadSize';
import { processAndSetImage } from 'utils/documentUpload';
import CustomDatePicker from 'app/atoms/CustomDatePicker';
import dayjs from 'dayjs';
import BannerLangPicker from 'app/molecules/BannerLangPicker/BannerLangPicker';
import { MAX_FILE_SIZE_MB } from 'configs';
import { PERMISSIONS_SUBJECTS } from '../Auth/constants';
import { PermissionActions } from '../Auth/types';

interface BannerFieldsProps {
  form: FormInstance;
  children?: React.ReactNode;
  selectedLang: string;
  onLangChange: (lang: string) => void;
  titleCount: number;
  onTitleChange: (count: number) => void;
}

export interface BannerFieldsRef {
  setContent: (content: string) => void;
  setImage: (imageUrl: string | null, file?: File) => void;
}

const BannerFields = forwardRef<BannerFieldsRef, BannerFieldsProps>(
  ({ children, form, selectedLang, onLangChange, titleCount, onTitleChange }, ref) => {
    const { t } = useTranslation();
    const [contentCount, setContentCount] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const [activeFormats, setActiveFormats] = useState({
      bold: false,
      italic: false,
      justifyCenter: false,
      justifyRight: false,
      justifyLeft: false,
    });

    const [currentColor, setCurrentColor] = useState('text-editor-black');
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    const colorMap = [
      { bg: 'bg-editor-black', text: 'text-editor-black' },
      { bg: 'bg-editor-darkGray', text: 'text-editor-darkGray' },
      { bg: 'bg-editor-gray', text: 'text-editor-gray' },
      { bg: 'bg-editor-lightGray', text: 'text-editor-lightGray' },
      { bg: 'bg-editor-red', text: 'text-editor-red' },
      { bg: 'bg-editor-lightRed', text: 'text-editor-lightRed' },
      { bg: 'bg-editor-orange', text: 'text-editor-orange' },
      { bg: 'bg-editor-darkOrange', text: 'text-editor-darkOrange' },
      { bg: 'bg-editor-green', text: 'text-editor-green' },
      { bg: 'bg-editor-blue', text: 'text-editor-blue' },
      { bg: 'bg-editor-purple', text: 'text-editor-purple' },
      { bg: 'bg-editor-pink', text: 'text-editor-pink' },
    ];

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onTitleChange(value.length);
    };

    const handleContentChange = () => {
      if (contentRef.current) {
        // Enforce max length
        const text = contentRef.current.innerText.replace(/\n/g, '').trim();
        if (text.length > 150) {
          trimContentToMaxLength(150);
        }
        setContentCount(text.length);
        // Update form field value
        form.setFieldsValue({
          content: contentRef.current.innerHTML,
        });
        // Update active formats
        setActiveFormats({
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          justifyCenter: document.queryCommandState('justifyCenter'),
          justifyRight: document.queryCommandState('justifyRight'),
          justifyLeft: document.queryCommandState('justifyLeft'),
        });
      }
    };

    const getContentTextLength = () => {
      if (contentRef.current) {
        // Remove all line breaks and trim whitespace
        const text = contentRef.current.innerText.replace(/\n/g, '').trim();
        return text.length;
      }
      return 0;
    };

    const getLineCount = () => {
      if (contentRef.current) {
        // Count the number of lines (split by \n or <div> or <br>)
        const html = contentRef.current.innerHTML;
        // Replace <div> and <br> with \n, then split
        const normalized = html.replace(/<div><br><\/div>|<div>|<\/div>|<br>/g, '\n');
        const lines = normalized.split(/\n/).filter((line) => line.trim() !== '');
        return lines.length;
      }
      return 0;
    };

    const trimContentToMaxLength = (maxLength: number) => {
      if (contentRef.current) {
        let text = contentRef.current.innerText.replace(/\n/g, '').trim();
        if (text.length > maxLength) {
          text = text.slice(0, maxLength);
          contentRef.current.innerText = text;
          handleContentChange();
        }
      }
    };

    const handleFormatting = (command: string) => {
      if (contentRef.current) {
        const selection = window.getSelection();
        if (selection) {
          document.execCommand(command, false);
          handleContentChange();
        }
      }
    };

    const handleAlignment = (command: string) => {
      if (contentRef.current) {
        document.execCommand('formatBlock', false, 'div');
        document.execCommand(command, false);
        handleContentChange();
      }
    };

    const handleColorChange = (textColor: string) => {
      if (contentRef.current) {
        contentRef.current.focus();

        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);

          if (!range.collapsed) {
            // A selection exists. We'll manipulate a copy of the selection to preserve structure.
            const container = document.createElement('div');
            container.appendChild(range.cloneContents());

            // 1. First, unwrap any existing color spans to start fresh.
            container.querySelectorAll('span[class*="text-editor-"]').forEach((span) => {
              if (span.parentNode) {
                while (span.firstChild) {
                  span.parentNode.insertBefore(span.firstChild, span);
                }
                span.parentNode.removeChild(span);
              }
            });

            // 2. Wrap all text nodes in the new color.
            const textWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
            const textNodes = [];
            while (textWalker.nextNode()) {
              if (textWalker.currentNode.textContent?.trim() !== '') {
                textNodes.push(textWalker.currentNode);
              }
            }
            textNodes.forEach((textNode) => {
              const span = document.createElement('span');
              span.className = textColor;
              textNode.parentNode?.insertBefore(span, textNode);
              span.appendChild(textNode);
            });

            // 3. Replace the original selection with the modified content.
            range.deleteContents();
            range.insertNode(container);
          } else {
            // No text selected (just a cursor). Insert a span and move cursor inside.
            const span = document.createElement('span');
            span.className = textColor;
            span.innerHTML = '&#8203;'; // Zero-width space
            range.insertNode(span);

            // Move the cursor inside the new span.
            const newRange = document.createRange();
            newRange.selectNodeContents(span);
            newRange.collapse(false); // Collapse to the end.
            selection.removeAllRanges();
            selection.addRange(newRange);
          }

          setCurrentColor(textColor);
          handleContentChange();
        }
      }
    };

    const ColorPicker: React.FC = () => (
      <div className="p-2">
        <div className="grid grid-cols-4 gap-2">
          {colorMap.map((color) => (
            <button
              key={color.bg}
              className={`w-6 h-6 rounded-sm border-2 focus:outline-none hover:opacity-80 ${color.bg} ${
                currentColor === color.text ? 'border-blue-600 shadow-md' : 'border-gray-200'
              }`}
              onClick={() => handleColorChange(color.text)}
            />
          ))}
        </div>
      </div>
    );

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      let paste = e.clipboardData.getData('text');
      const currentText = contentRef.current?.innerText.replace(/\n/g, '').trim() || '';
      const allowed = 150 - currentText.length;

      if (allowed <= 0) return;

      // Only allow up to 3 lines, but if we're at line limit, allow single line paste
      let lines = paste.split(/\r?\n/).filter((line) => line.trim() !== '');
      const currentLineCount = getLineCount();

      if (currentLineCount >= 3) {
        // If already at 3 lines, only allow single line paste
        lines = lines.slice(0, 1);
      } else {
        // Otherwise, allow up to remaining line slots
        lines = lines.slice(0, 3 - currentLineCount);
      }

      paste = lines.join('\n');
      const toPaste = paste.slice(0, allowed);
      document.execCommand('insertText', false, toPaste);
      handleContentChange();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      const textLength = getContentTextLength();
      const lineCount = getLineCount();
      // Allow navigation, backspace, delete, etc.
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'];
      if (
        textLength >= 150 &&
        !allowedKeys.includes(e.key) &&
        !(e.ctrlKey && (e.key === 'a' || e.key === 'c' || e.key === 'x' || e.key === 'v'))
      ) {
        e.preventDefault();
        return;
      }
      // Prevent Enter if already 3 lines
      if (e.key === 'Enter' && lineCount >= 3) {
        e.preventDefault();
        return;
      }
      if (e.key === 'b' && e.ctrlKey) {
        e.preventDefault();
        document.execCommand('bold', false);
        handleContentChange();
      } else if (e.key === 'i' && e.ctrlKey) {
        e.preventDefault();
        document.execCommand('italic', false);
        handleContentChange();
      }
    };

    const setContent = (content: string) => {
      if (contentRef.current) {
        contentRef.current.innerHTML = content;
        setContentCount(contentRef.current.innerText.length);
      }
    };

    const setImage = (imageUrl: string | null, file?: File) => {
      setImageUrl(imageUrl);
      if (imageUrl) {
        form.setFieldsValue({
          image: [
            {
              url: imageUrl,
              name: 'Banner Image',
              ...(file && { originFileObj: file }),
            },
          ],
          imageUrl: imageUrl,
        });
      } else {
        form.setFieldsValue({
          image: undefined,
          imageUrl: '',
        });
      }
    };

    const handleProcessAndSetImage = async (file: File) => {
      await processAndSetImage({
        file,
        t,
        setImage,
      });
    };

    useImperativeHandle(ref, () => ({
      setContent,
      setImage,
    }));

    return (
      <>
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <div className="flex justify-end">
              <BannerLangPicker value={selectedLang} onChange={onLangChange} />
            </div>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          {/* Title */}
          <Col xs={24}>
            <Form.Item
              label={t('banner.fields.title')}
              name="title"
              rules={[{ required: true, message: t('banner.rules.title') }]}
              extra={<div className="text-gray-650 italic font-medium text-left rtl:text-right">{titleCount}/80</div>}
            >
              <Input
                {...fieldDefaultProps}
                placeholder={t('banner.fields.placeholders.title')}
                data-testid="title-input"
                maxLength={80}
                onChange={handleTitleChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          {/* Content */}
          <Col xs={24}>
            <Form.Item
              label={t('banner.fields.content')}
              name="content"
              rules={[{ required: true, message: t('banner.rules.content') }]}
              extra={
                <div className="text-gray-650 italic font-medium mt-1 text-left rtl:text-right">{contentCount}/150</div>
              }
            >
              <div className="relative border border-gray-400 rounded-md">
                <div className="flex items-center gap-2 p-2 border-b border-gray-400 bg-gray-100">
                  <Button
                    type="text"
                    icon={<BoldOutlined />}
                    onClick={() => handleFormatting('bold')}
                    data-testid="bold-button"
                    className={`hover:bg-gray-200 ${activeFormats.bold ? 'bg-gray-200 text-primary-600' : ''}`}
                  />
                  <Button
                    type="text"
                    icon={<ItalicOutlined />}
                    onClick={() => handleFormatting('italic')}
                    data-testid="italic-button"
                    className={`hover:bg-gray-200 ${activeFormats.italic ? 'bg-gray-200 text-primary-600' : ''}`}
                  />
                  <Popover
                    content={<ColorPicker />}
                    trigger="click"
                    placement="bottom"
                    overlayClassName="color-picker-popover"
                  >
                    <Button
                      type="text"
                      icon={<FontColorsOutlined style={{ color: currentColor }} />}
                      data-testid="color-button"
                      className="hover:bg-gray-200"
                    />
                  </Popover>
                  <div className="h-5 w-px bg-gray-300 mx-1" />
                  <Button
                    type="text"
                    icon={<AlignLeftOutlined />}
                    onClick={() => handleAlignment('justifyLeft')}
                    data-testid="align-left-button"
                    className={`hover:bg-gray-200 ${activeFormats.justifyLeft ? 'bg-gray-200 text-primary-600' : ''}`}
                  />
                  <Button
                    type="text"
                    icon={<AlignCenterOutlined />}
                    onClick={() => handleAlignment('justifyCenter')}
                    data-testid="align-center-button"
                    className={`hover:bg-gray-200 ${activeFormats.justifyCenter ? 'bg-gray-200 text-primary-600' : ''}`}
                  />
                  <Button
                    type="text"
                    icon={<AlignRightOutlined />}
                    onClick={() => handleAlignment('justifyRight')}
                    data-testid="align-right-button"
                    className={`hover:bg-gray-200 ${activeFormats.justifyRight ? 'bg-gray-200 text-primary-600' : ''}`}
                  />
                </div>
                <div
                  ref={contentRef}
                  contentEditable
                  className="min-h-[100px] p-4 focus:outline-none"
                  onInput={handleContentChange}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onSelect={handleContentChange}
                  data-testid="content-editor"
                />
              </div>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          {/* Banner Image */}
          <Col xs={24}>
            <Form.Item label={t('banner.fields.image')} name="image">
              <div>
                {imageUrl ? (
                  <div className="w-full max-w-[1120px] h-[100px] md:h-[150px] overflow-hidden">
                    <img src={imageUrl} alt="Banner" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <Card className="relative w-full max-w-[1120px] h-[100px] md:h-[150px] mb-2 flex items-center justify-center">
                    <div className="flex items-center justify-center w-[96px] h-[96px] bg-gray-300">
                      <PictureOutlined className="text-6xl text-gray-600" />
                    </div>
                  </Card>
                )}

                <Authorize
                  requiredPermissions={[
                    { action: PermissionActions.EDIT_BANNERS, subject: PERMISSIONS_SUBJECTS.BANNERS },
                  ]}
                >
                  <div className="text-gray-650 mb-2 italic">
                    {t('banner.rules.image', { maxSize: MAX_FILE_SIZE_MB })}
                  </div>
                  <Upload
                    {...fieldDefaultProps}
                    maxCount={MAX_PROFILE_PICTURE_COUNT}
                    accept={IMAGE_TYPE_WITHOUT_PDF}
                    beforeUpload={async (file) => {
                      const uploadSizeResult = validateUploadSize(file, t, false);
                      if (uploadSizeResult) {
                        return Upload.LIST_IGNORE;
                      }

                      // Process and resize the image
                      await handleProcessAndSetImage(file);

                      return false; // Prevent default upload behavior
                    }}
                    showUploadList={false}
                    data-testid="banner-image-upload"
                    className="border-primary-600"
                  >
                    <Button color="primary" variant="outlined" data-testid="upload-image-button">
                      <h5 className="h5-semibold text-primary-600">{t('banner.fields.uploadImage')}</h5>
                    </Button>
                  </Upload>
                </Authorize>
              </div>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          {/* Link */}
          <Col xs={24}>
            <Form.Item label={t('banner.fields.link')} name="link">
              <Input {...fieldDefaultProps} placeholder="eg. http://" data-testid="link-input" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          {/* Start Date */}
          <Col xs={24} lg={12}>
            <Form.Item
              label={t('banner.fields.startDate')}
              name="startDate"
              rules={[{ required: true, message: t('banner.rules.startDate') }]}
            >
              <DatePicker
                {...fieldDefaultProps}
                placeholder={t('banner.fields.placeholders.startDate')}
                data-testid="start-date-picker"
                format={(date) => formatDateWithLocale(date, DATE_FORMAT)}
                cellRender={renderDateWithLocale}
                disabledDate={isDisableBeforeCurrentDate}
                onChange={() => {
                  // Clear end date when start date changes
                  form.setFieldValue('endDate', null);
                }}
              />
            </Form.Item>
          </Col>

          {/* End Date */}
          <Col xs={24} lg={12}>
            <Form.Item
              label={t('banner.fields.endDate')}
              name="endDate"
              rules={[
                { required: true, message: t('banner.rules.endDate') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue('startDate');

                    if (!startDate || !value) {
                      return Promise.resolve();
                    }

                    const startDateDayJs = dayjs(startDate).startOf('day');
                    const endDateDayJs = dayjs(value).startOf('day');
                    if (endDateDayJs.isBefore(startDateDayJs) || endDateDayJs.isSame(startDateDayJs)) {
                      return Promise.reject(t('banner.rules.endDateAfterStartDate'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <CustomDatePicker
                {...fieldDefaultProps}
                placeholder={t('banner.fields.placeholders.endDate')}
                data-testid="end-date-picker"
                disabledDate={(current) => {
                  const startDate = form.getFieldValue('startDate');
                  return current && (!startDate || current <= dayjs(startDate).startOf('day'));
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {children}
      </>
    );
  },
);

BannerFields.displayName = 'BannerFields';

export default BannerFields;
